/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactIconButton, {
  TrueImpactIconButtonProps,
} from 'shared/design-system/components/TrueImpactIconButton';
import {
  FeedbackCommentProps,
  FeedbackSectionId,
  UserType,
  userTypeToString,
} from './utils';
import FeedbackSectionTextarea from './FeedbackSectionTextarea';
import ApolloClient from 'apollo-client';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { FetchResult } from 'apollo-boost';
import { formattedDateTime } from 'shared/utils/date';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { parseFeedbackModelDetails } from './FeedbackCommentTextarea';
import { stageIdToString } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { SuccessResponse } from 'npo/pages/ReportsBeta/NewReport/api';
import TrueImpactTag from 'shared/design-system/components/TrueImpactTag';

export interface FeedbackCommentBaseProps {
  comment: FeedbackCommentProps;
  reportId: number;
  sectionId: FeedbackSectionId;
  index: number;
  className?: string;
  currentUserId?: string;
  currentUserName: string;
  currentUserEmail?: string;
  currentUserType: UserType;
  hideModelButton?: boolean;
  isNew?: boolean;
  handleSave: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
    details?: string,
  ) => Promise<
    FetchResult<
      SuccessResponse<'updateComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  handleDelete: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
  ) => Promise<
    FetchResult<
      SuccessResponse<'deleteComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  onSuccessDelete: (index: number) => unknown;
  onSuccessSave: (comment: FeedbackCommentProps, index: number) => unknown;
}

const dotPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

const StyledDiv = styled.div<{ isCurrentUserComment: boolean; isNew?: boolean }>`
  padding: ${SPACERS.medium};
  max-width: 510px;
  margin-bottom: 20px;
  ${({ isCurrentUserComment }) =>
    isCurrentUserComment ? 'margin-left: auto;' : ''}
  border-radius: ${({ isCurrentUserComment }) =>
    isCurrentUserComment ? '24px 24px 0px 24px' : '24px 24px 24px 0px'};
  background-color: ${({ isCurrentUserComment }) =>
    isCurrentUserComment ? COLORS.ctaPrimary : COLORS.grayDarkest};
  ${({ isNew }) =>
    isNew
      ? `box-shadow: 0 0 0 2px ${COLORS.highlight}; border: 1px solid ${COLORS.highlight};`
      : ''}
  .new-comment-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-left: ${SPACERS.small};
    border: 2px solid ${COLORS.whiteBase};
    background-color: ${COLORS.whiteBase};
    animation: ${dotPulse} 1.8s ease-out infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
  .comment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    .comment-info {
      display: flex;
      align-items: center;
    }
  }
  .comment-details {
    margin-bottom: ${SPACERS.tiny};
  }
`;

const unknownText = 'Unknown';
const FeedbackComment: React.FC<FeedbackCommentBaseProps> = ({
  reportId,
  sectionId,
  index,
  currentUserEmail,
  comment,
  className,
  hideModelButton,
  isNew,
  handleDelete,
  onSuccessDelete,
  handleSave,
  onSuccessSave,
}) => {
  const {
    userId,
    commentId,
    text,
    name,
    details,
    createdAt,
    email,
    accountType,
  } = comment;
  const client = useClient();
  const [edit, setEdit] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>();
  const [
    saveRequestState,
    makeSaveRequest,
    setSaveRequestState,
  ] = useGraphQLRequest<SuccessResponse<'updateComment'>>();
  const [
    deleteRequestState,
    makeDeleteRequest,
    setDeleteRequestState,
  ] = useGraphQLRequest();

  const handleCancel = () => setEdit(false);

  const onSave = useCallback(
    (commentText: string) => {
      setCommentText(commentText);
      makeSaveRequest(
        handleSave(
          client!,
          commentId,
          commentText,
          reportId,
          sectionId,
          details,
        ),
      );
    },
    [
      makeSaveRequest,
      handleSave,
      setCommentText,
      client,
      commentId,
      reportId,
      sectionId,
      details,
    ],
  );

  const onDelete = useCallback(() => {
    makeDeleteRequest(
      handleDelete(client!, commentId, text, reportId, sectionId),
    );
  }, [
    makeSaveRequest,
    handleSave,
    setCommentText,
    text,
    client,
    commentId,
    reportId,
    sectionId,
  ]);

  useEffect(() => {
    if (deleteRequestState.type !== 'REQUEST_SUCCESS') return;
    onSuccessDelete(index);
    setDeleteRequestState(requestInit());
  }, [index, deleteRequestState.type, onSuccessDelete, setDeleteRequestState]);

  useEffect(() => {
    if (saveRequestState.type !== 'REQUEST_SUCCESS' || !commentText) return;
    onSuccessSave(
      {
        userId: comment.userId,
        commentId,
        createdAt,
        name,
        text: commentText,
        accountType,
        email: currentUserEmail,
        updatedAt: new Date().toDateString(),
        details,
      },
      index,
    );
    setEdit(false);
    setSaveRequestState(requestInit());
  }, [
    name,
    index,
    email,
    commentId,
    details,
    createdAt,
    commentText,
    accountType,
    saveRequestState.type,
    setEdit,
    onSuccessSave,
    setSaveRequestState,
  ]);

  const errorMessage =
    saveRequestState.type === 'REQUEST_ERROR'
      ? saveRequestState.error.message
      : undefined;

  return (
    <FeedbackCommentRender
      edit={edit}
      text={text}
      name={name}
      email={email}
      userId={userId}
      details={details}
      createdAt={createdAt}
      className={className}
      accountType={accountType}
      errorMessage={errorMessage}
      hideModelButton={hideModelButton}
      isNew={isNew}
      onSave={onSave}
      setEdit={setEdit}
      onDelete={onDelete}
      handleCancel={handleCancel}
    />
  );
};

export default FeedbackComment;

interface FeedbackCommentRenderProps {
  name: string;
  text: string;
  details?: string;
  edit: boolean;
  userId: number;
  createdAt: string;
  className?: string;
  email: string | undefined;
  hideModelButton?: boolean;
  isNew?: boolean;
  errorMessage: string | undefined;
  accountType: UserType | undefined;
  onDelete: () => unknown;
  handleCancel: () => unknown;
  onSave: (commentText: string) => unknown;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FeedbackCommentRender: React.FC<FeedbackCommentRenderProps> = ({
  name,
  edit,
  text,
  email,
  details,
  createdAt,
  className,
  accountType,
  errorMessage,
  hideModelButton,
  isNew,
  onSave,
  setEdit,
  onDelete,
  handleCancel,
  userId,
}) => {
  const d = parseFeedbackModelDetails(details);
  const {
    email: currentUserEmail,
    ghostedUsername,
    userId: currentUserId,
  } = useUserActions();
  const e = ghostedUsername || currentUserEmail;
  const isCurrentUserComment =
    email === e || userId.toString() === currentUserId.toString();

  const backgroundIconColor:
    | TrueImpactIconButtonProps['backgroundColor']
    | undefined = isCurrentUserComment ? 'whiteBase' : 'grayBase';

  const showNew = isNew && !isCurrentUserComment;
  return (
    <StyledDiv
      isNew={showNew}
      isCurrentUserComment={isCurrentUserComment}
      className={className}
    >
      <div className="comment-header">
        <div className="comment-info">
          <TrueImpactText color="whiteBase" variant="body-bold">
            {name || unknownText}
          </TrueImpactText>
          <TrueImpactTag accountType={accountType} />
          <TrueImpactText color="whiteBase" variant="body-small">
            {formattedDateTime(createdAt) || unknownText}
          </TrueImpactText>
          {showNew && (
            <span
              className="new-comment-dot"
              title="Latest comment"
              aria-label="Latest comment"
            />
          )}
        </div>
        {isCurrentUserComment && !edit && (
          <div className="comment-action-buttons">
            <TrueImpactIconButton
              icon={faPen}
              label="Edit"
              color={backgroundIconColor}
              onClick={() => setEdit(true)}
            />
            <TrueImpactIconButton
              icon={faTrash}
              label="Delete"
              color={backgroundIconColor}
              onClick={onDelete}
            />
          </div>
        )}
      </div>
      {!hideModelButton && (d.indicatorName || d.sectionId) ? (
        <TrueImpactText
          color="grayBase"
          variant="body-small"
          className="comment-details"
        >
          {d.sectionId ? `Section: ${stageIdToString(d.sectionId)}` : ''}
          {d.sectionId && d.indicatorName ? ' - ' : ''}
          {d.indicatorName ? `Indicator: ${d.indicatorName}` : ''}
        </TrueImpactText>
      ) : null}
      {edit ? (
        <FeedbackSectionTextarea
          id="feedback-section-level-comment"
          initialValue={text}
          autoFocus={true}
          errorMessage={errorMessage}
          placeholder="Leave feedback here..."
          onSave={onSave}
          onCancel={handleCancel}
        />
      ) : (
        <TrueImpactText color="whiteBase" className="comment-text">
          {text}
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};
